import React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  LayoutDashboard,
  Loader2,
  LogOut,
  Mail,
  User,
} from "lucide-react";
import {
  formatTeacherValue,
  getTeacherDisplayName,
  getTeacherEmail,
  humanizeTeacherField,
} from "../../features/teacher/teacherApi";

const teacherAuthStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-auth-page {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 24px 16px;
    position: relative;
    overflow: hidden;
    background:
      radial-gradient(circle at center, rgba(255, 255, 255, 0.32), transparent 48%),
      linear-gradient(180deg, #fcf8f1 0%, #f6f1e7 100%);
    font-family: "Lato", sans-serif;
  }

  .teacher-auth-page::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
    background-size: 32px 32px;
    opacity: 0.35;
    pointer-events: none;
  }

  .teacher-auth-halo {
    position: absolute;
    border-radius: 999px;
    filter: blur(12px);
    pointer-events: none;
  }

  .teacher-auth-halo-left {
    width: 300px;
    height: 300px;
    left: clamp(-72px, 5vw, 48px);
    top: 11vh;
    background: rgba(166, 210, 255, 0.58);
  }

  .teacher-auth-halo-right {
    width: 260px;
    height: 260px;
    right: clamp(-44px, 7vw, 120px);
    top: 18vh;
    background: rgba(226, 190, 255, 0.45);
  }

  .teacher-auth-halo-bottom {
    width: 250px;
    height: 250px;
    right: 20vw;
    bottom: 6vh;
    background: rgba(255, 190, 220, 0.5);
  }

  .teacher-auth-card {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 28px;
    width: min(100%, 520px);
    padding: 32px;
    overflow: hidden;
    border-radius: 16px;
    border: 1px solid rgba(244, 240, 236, 0.96);
    background: rgba(255, 255, 255, 0.96);
    box-shadow:
      0 28px 60px rgba(104, 78, 29, 0.16),
      0 10px 22px rgba(23, 18, 14, 0.06);
    backdrop-filter: blur(18px);
  }

  .teacher-auth-card-wide {
    width: min(100%, 680px);
  }

  .teacher-auth-card-compact {
    gap: 18px;
    padding: 22px 26px;
  }

  .teacher-auth-brand {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .teacher-auth-badge {
    width: 76px;
    height: 76px;
    display: inline-grid;
    place-items: center;
    margin-bottom: 16px;
    border-radius: 999px;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 14px 24px rgba(255, 86, 77, 0.2);
    color: #ffffff;
  }

  .teacher-auth-title {
    margin: 0;
    color: #ff624d;
    font-size: 2rem;
    line-height: 1.2;
    font-weight: 800;
  }

  .teacher-auth-subtitle {
    margin: 8px 0 0;
    color: #383534;
    font-size: 0.98rem;
    font-weight: 400;
  }

  .teacher-auth-form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .teacher-auth-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
  }

  .teacher-auth-grid-full {
    grid-column: 1 / -1;
  }

  .teacher-auth-field {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .teacher-auth-label {
    color: #1f1a17;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .teacher-auth-input-shell {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 52px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid #dde2ea;
    background: #ffffff;
    transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
  }

  .teacher-auth-input-shell:focus-within {
    border-color: rgba(255, 98, 77, 0.55);
    box-shadow: 0 0 0 4px rgba(255, 98, 77, 0.12);
    transform: translateY(-1px);
  }

  .teacher-auth-input-shell-error {
    border-color: rgba(225, 74, 74, 0.55);
    box-shadow: 0 0 0 4px rgba(225, 74, 74, 0.1);
  }

  .teacher-auth-input-shell svg {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    color: #9fa9ba;
  }

  .teacher-auth-input,
  .teacher-auth-select,
  .teacher-auth-textarea {
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.95rem;
  }

  .teacher-auth-textarea {
    min-height: 120px;
    padding: 14px 0;
    resize: vertical;
  }

  .teacher-auth-select {
    appearance: none;
  }

  .teacher-auth-toggle {
    display: grid;
    place-items: center;
    padding: 0;
    border: 0;
    background: transparent;
    color: #9fa9ba;
    cursor: pointer;
  }

  .teacher-auth-toggle:hover,
  .teacher-auth-toggle:focus-visible {
    color: #6b7384;
  }

  .teacher-auth-note {
    margin: 0;
    color: #7a7673;
    font-size: 0.86rem;
  }

  .teacher-auth-field-error {
    margin: 0;
    color: #c23d3d;
    font-size: 0.84rem;
    font-weight: 600;
  }

  .teacher-auth-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .teacher-auth-link {
    color: #275df3;
    font-size: 0.94rem;
    font-weight: 500;
    text-decoration: none;
  }

  .teacher-auth-link:hover,
  .teacher-auth-link:focus-visible {
    text-decoration: underline;
  }

  .teacher-auth-button {
    width: 100%;
    min-height: 48px;
    border: 0;
    border-radius: 10px;
    background: linear-gradient(135deg, #ff624d 0%, #ef4b43 100%);
    box-shadow: 0 14px 26px rgba(255, 98, 77, 0.26);
    color: #ffffff;
    cursor: pointer;
    font: inherit;
    font-size: 0.99rem;
    font-weight: 800;
    transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
  }

  .teacher-auth-button:hover,
  .teacher-auth-button:focus-visible {
    transform: translateY(-1px);
    box-shadow: 0 22px 32px rgba(255, 98, 77, 0.3);
    filter: saturate(1.05);
  }

  .teacher-auth-button:disabled {
    cursor: wait;
    opacity: 0.7;
    transform: none;
    box-shadow: 0 14px 26px rgba(255, 98, 77, 0.2);
  }

  .teacher-auth-footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    text-align: center;
  }

  .teacher-auth-footer-text {
    margin: 0;
    color: #4d6476;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .teacher-inline-message {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.95rem 1rem;
    border-radius: 0.9rem;
    font-size: 0.92rem;
    font-weight: 600;
  }

  .teacher-inline-message-info {
    background: #eef4ff;
    color: #2c5bc5;
    border: 1px solid #d6e2ff;
  }

  .teacher-inline-message-success {
    background: #eaf8ef;
    color: #1b8b58;
    border: 1px solid #c9eed8;
  }

  .teacher-inline-message-error {
    background: #fff1f1;
    color: #c23d3d;
    border: 1px solid #ffd3d3;
  }

  @media (max-width: 640px) {
    .teacher-auth-card {
      width: min(100%, 520px);
      padding: 28px 20px 24px;
      gap: 24px;
    }

    .teacher-auth-title {
      font-size: 1.85rem;
    }

    .teacher-auth-grid {
      grid-template-columns: 1fr;
    }

    .teacher-auth-halo {
      opacity: 0.85;
    }
  }
`;

const teacherAppStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-page {
    min-height: 100vh;
    background: #faf5ec;
    color: #1f1a17;
    font-family: "Lato", sans-serif;
    font-size: 1rem;
  }

  .teacher-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #ebe4d8;
    backdrop-filter: blur(14px);
  }

  .teacher-shell {
    width: min(100%, 78rem);
    margin: 0 auto;
    padding-inline: clamp(0.875rem, 2vw, 1.25rem);
  }

  .teacher-topbar-row {
    min-height: clamp(4rem, 5.5vw, 4.5rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: clamp(1rem, 2vw, 1.5rem);
  }

  .teacher-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-brand-badge {
    width: clamp(2.25rem, 2.8vw, 2.45rem);
    height: clamp(2.25rem, 2.8vw, 2.45rem);
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.2);
  }

  .teacher-brand-copy h1 {
    margin: 0;
    color: #ff624d;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    font-weight: 700;
  }

  .teacher-brand-copy p {
    margin: 2px 0 0;
    color: #7a7673;
    font-size: 0.88rem;
  }

  .teacher-topbar-actions {
    display: flex;
    align-items: center;
    gap: 0.9rem;
  }

  .teacher-email-pill,
  .teacher-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.5rem;
    padding-inline: 1rem;
    border: 1px solid #dbe3f2;
    border-radius: 999px;
    background: #ffffff;
    color: #6f6a67;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .teacher-logout {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    border: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
  }

  .teacher-tabs {
    display: flex;
    align-items: center;
    gap: clamp(0.625rem, 1.5vw, 0.95rem);
    min-height: 3.35rem;
  }

  .teacher-tab {
    min-height: 3.35rem;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    padding-inline: 0.95rem;
    border: 0;
    border-bottom: 0.1875rem solid transparent;
    background: transparent;
    color: #2f2a28;
    font: inherit;
    font-size: 0.95rem;
    text-decoration: none;
    cursor: pointer;
  }

  .teacher-tab-active {
    color: #2453d4;
    border-bottom-color: #2453d4;
    background: rgba(226, 235, 255, 0.78);
    border-top-left-radius: 0.875rem;
    border-top-right-radius: 0.875rem;
  }

  .teacher-content {
    padding: clamp(1.25rem, 2.5vw, 1.65rem) clamp(0.875rem, 1.6vw, 1.25rem)
      clamp(2rem, 4vw, 2.75rem);
  }

  .teacher-banner {
    border: 1px solid #d7e3fb;
    border-radius: 1rem;
    background: #dfe8fb;
    padding: clamp(1.25rem, 2vw, 1.6rem);
    margin-bottom: 1.5rem;
  }

  .teacher-banner h2 {
    margin: 0 0 0.75rem;
    font-size: clamp(0.95rem, 1.3vw, 1.04rem);
    font-weight: 600;
  }

  .teacher-banner p {
    margin: 0;
    color: #75706c;
    font-size: 0.92rem;
  }

  .teacher-grid {
    display: grid;
    gap: 1.25rem;
  }

  .teacher-grid-two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .teacher-grid-three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .teacher-card {
    border: 1px solid #dde5ef;
    border-radius: 1rem;
    background: #ffffff;
    padding: clamp(1rem, 1.8vw, 1.3rem);
    box-shadow: 0 18px 34px rgba(53, 39, 24, 0.04);
  }

  .teacher-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .teacher-card-header h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
  }

  .teacher-card-header p {
    margin: 0.35rem 0 0;
    color: #7a7673;
    font-size: 0.9rem;
  }

  .teacher-stat-card {
    min-height: 7rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 0.85rem;
  }

  .teacher-stat-value {
    font-size: 1.8rem;
    font-weight: 800;
  }

  .teacher-muted {
    color: #7a7673;
  }

  .teacher-section-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .teacher-button,
  .teacher-button-secondary {
    min-height: 2.9rem;
    padding: 0 1rem;
    border-radius: 0.85rem;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 700;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    cursor: pointer;
    transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
  }

  .teacher-button {
    border: 0;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 14px 24px rgba(255, 98, 77, 0.2);
    color: #ffffff;
  }

  .teacher-button-secondary {
    border: 1px solid #d7e3fb;
    background: #ffffff;
    color: #2453d4;
  }

  .teacher-button:hover,
  .teacher-button-secondary:hover {
    transform: translateY(-1px);
  }

  .teacher-button:disabled,
  .teacher-button-secondary:disabled {
    cursor: wait;
    opacity: 0.7;
    transform: none;
  }

  .teacher-form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .teacher-form-grid-full {
    grid-column: 1 / -1;
  }

  .teacher-field-group {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  .teacher-field-group label {
    color: #1f1a17;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .teacher-input,
  .teacher-select,
  .teacher-textarea {
    width: 100%;
    min-height: 3rem;
    border: 1px solid #dde5ef;
    border-radius: 0.75rem;
    background: #ffffff;
    color: #1f1a17;
    font: inherit;
    font-size: 0.95rem;
    padding: 0 1rem;
  }

  .teacher-textarea {
    min-height: 8.5rem;
    padding: 0.9rem 1rem;
    resize: vertical;
  }

  .teacher-input-error,
  .teacher-select-error,
  .teacher-textarea-error {
    border-color: rgba(225, 74, 74, 0.55);
    box-shadow: 0 0 0 4px rgba(225, 74, 74, 0.1);
  }

  .teacher-help-text,
  .teacher-field-error {
    margin: 0;
    font-size: 0.84rem;
  }

  .teacher-help-text {
    color: #7a7673;
  }

  .teacher-field-error {
    color: #c23d3d;
    font-weight: 600;
  }

  .teacher-inline-message {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.95rem 1rem;
    border-radius: 0.9rem;
    font-size: 0.92rem;
    font-weight: 600;
  }

  .teacher-inline-message-info {
    background: #eef4ff;
    color: #2c5bc5;
    border: 1px solid #d6e2ff;
  }

  .teacher-inline-message-success {
    background: #eaf8ef;
    color: #1b8b58;
    border: 1px solid #c9eed8;
  }

  .teacher-inline-message-error {
    background: #fff1f1;
    color: #c23d3d;
    border: 1px solid #ffd3d3;
  }

  .teacher-loading-state,
  .teacher-empty-state {
    min-height: 220px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.9rem;
    text-align: center;
    color: #7a7673;
  }

  .teacher-loading-state svg {
    animation: teacher-spin 1s linear infinite;
  }

  @keyframes teacher-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .teacher-key-value-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.95rem 1rem;
  }

  .teacher-key-value-item {
    border: 1px solid #edf1f7;
    border-radius: 0.8rem;
    padding: 0.9rem 1rem;
    background: #fbfcff;
  }

  .teacher-key-value-label {
    display: block;
    color: #7a7673;
    font-size: 0.82rem;
    margin-bottom: 0.35rem;
  }

  .teacher-key-value-value {
    display: block;
    color: #1f1a17;
    font-size: 0.95rem;
    font-weight: 700;
    word-break: break-word;
  }

  .teacher-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .teacher-list-card {
    border: 1px solid #dde5ef;
    border-radius: 1rem;
    background: #ffffff;
    padding: 1rem;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .teacher-list-card-main {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    min-width: 0;
  }

  .teacher-avatar {
    width: 3rem;
    height: 3rem;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: #000000;
    color: #ffffff;
    font-size: 1rem;
    font-weight: 800;
    flex: 0 0 auto;
  }

  .teacher-list-card-heading {
    display: flex;
    align-items: center;
    gap: 0.85rem;
  }

  .teacher-list-card-heading h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
  }

  .teacher-list-card-heading p {
    margin: 0.25rem 0 0;
    color: #7a7673;
    font-size: 0.9rem;
  }

  .teacher-chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .teacher-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 2rem;
    padding: 0 0.8rem;
    border-radius: 999px;
    background: #f5f7fb;
    color: #3d3a39;
    font-size: 0.88rem;
    font-weight: 600;
  }

  .teacher-code-block {
    margin: 0;
    padding: 0.95rem 1rem;
    border-radius: 0.9rem;
    background: #10151f;
    color: #f6f8fb;
    font-size: 0.84rem;
    overflow-x: auto;
  }

  .teacher-toolbar {
    display: flex;
    align-items: flex-end;
    gap: 1rem;
    flex-wrap: wrap;
  }

  @media (max-width: 980px) {
    .teacher-grid-two,
    .teacher-grid-three,
    .teacher-form-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .teacher-topbar-row {
      min-height: auto;
      padding: 1.125rem 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-topbar-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .teacher-email-pill,
    .teacher-status-pill,
    .teacher-logout {
      justify-content: center;
    }

    .teacher-tabs {
      overflow-x: auto;
      padding-bottom: 0.5rem;
    }

    .teacher-tab {
      white-space: nowrap;
    }
  }

  @media (max-width: 640px) {
    .teacher-key-value-grid {
      grid-template-columns: 1fr;
    }

    .teacher-list-card {
      flex-direction: column;
    }
  }
`;

const teacherTabs = [
  {
    key: "dashboard",
    label: "Dashboard",
    to: "/teacherdashboard",
    icon: LayoutDashboard,
  },
  {
    key: "availability",
    label: "Availability",
    to: "/teacheravailability",
    icon: CalendarDays,
  },
  {
    key: "profile",
    label: "Profile",
    to: "/teacherprofile",
    icon: User,
  },
];

export function TeacherAuthLayout({
  title,
  subtitle,
  children,
  footer,
  cardClassName = "",
  wide = false,
}) {
  return (
    <>
      <style>{teacherAuthStyles}</style>
      <main className="teacher-auth-page">
        <div className="teacher-auth-halo teacher-auth-halo-left" aria-hidden="true" />
        <div className="teacher-auth-halo teacher-auth-halo-right" aria-hidden="true" />
        <div className="teacher-auth-halo teacher-auth-halo-bottom" aria-hidden="true" />

        <section
          className={`teacher-auth-card${wide ? " teacher-auth-card-wide" : ""}${
            cardClassName ? ` ${cardClassName}` : ""
          }`}
        >
          <div className="teacher-auth-brand">
            <div className="teacher-auth-badge" aria-hidden="true">
              <BookOpen size={34} strokeWidth={2.1} />
            </div>
            <h1 className="teacher-auth-title">{title}</h1>
            <p className="teacher-auth-subtitle">{subtitle}</p>
          </div>

          {children}

          {footer ? <div className="teacher-auth-footer">{footer}</div> : null}
        </section>
      </main>
    </>
  );
}

export function TeacherAuthField({
  label,
  id,
  icon: Icon,
  error,
  note,
  trailingAction,
  as = "input",
  className = "",
  ...fieldProps
}) {
  const sharedClassName = `teacher-auth-${
    as === "textarea" ? "textarea" : as === "select" ? "select" : "input"
  } ${className}`.trim();

  const fieldElement =
    as === "textarea" ? (
      <textarea id={id} className={sharedClassName} {...fieldProps} />
    ) : as === "select" ? (
      <select id={id} className={sharedClassName} {...fieldProps} />
    ) : (
      <input id={id} className={sharedClassName} {...fieldProps} />
    );

  return (
    <div className="teacher-auth-field">
      <label className="teacher-auth-label" htmlFor={id}>
        {label}
      </label>
      <div
        className={`teacher-auth-input-shell${
          error ? " teacher-auth-input-shell-error" : ""
        }`}
      >
        {Icon ? <Icon aria-hidden="true" /> : null}
        {fieldElement}
        {trailingAction}
      </div>
      {note ? <p className="teacher-auth-note">{note}</p> : null}
      {error ? <p className="teacher-auth-field-error">{error}</p> : null}
    </div>
  );
}

export function TeacherFeedbackMessage({ variant = "info", children }) {
  if (!children) return null;

  const icon =
    variant === "success" ? (
      <CheckCircle2 size={18} />
    ) : variant === "error" ? (
      <AlertCircle size={18} />
    ) : (
      <Mail size={18} />
    );

  return (
    <div className={`teacher-inline-message teacher-inline-message-${variant}`}>
      {icon}
      <span>{children}</span>
    </div>
  );
}

export function TeacherPageShell({
  pageTitle = "Teacher Workspace",
  pageSubtitle = "Manage your teacher account and API-backed workflows.",
  activeTab,
  email,
  onLogout,
  children,
  tabs = teacherTabs,
  headerStatus,
}) {
  return (
    <>
      <style>{teacherAppStyles}</style>
      <main className="teacher-page">
        <header className="teacher-topbar">
          <div className="teacher-shell">
            <div className="teacher-topbar-row">
              <div className="teacher-brand">
                <div className="teacher-brand-badge" aria-hidden="true">
                  <BookOpen size={20} />
                </div>
                <div className="teacher-brand-copy">
                  <h1>{pageTitle}</h1>
                  <p>{pageSubtitle}</p>
                </div>
              </div>

              <div className="teacher-topbar-actions">
                {headerStatus ? (
                  <div className="teacher-status-pill">{headerStatus}</div>
                ) : null}
                <div className="teacher-email-pill">
                  <Mail size={18} />
                  {email || "Teacher session"}
                </div>
                {onLogout ? (
                  <button className="teacher-logout" type="button" onClick={onLogout}>
                    <LogOut size={18} />
                    Logout
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        {tabs?.length ? (
          <div className="teacher-shell">
            <nav className="teacher-tabs" aria-label="Teacher tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;

                return (
                  <Link
                    key={tab.key}
                    to={tab.to}
                    className={`teacher-tab${
                      activeTab === tab.key ? " teacher-tab-active" : ""
                    }`}
                  >
                    <Icon size={20} />
                    {tab.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        ) : null}

        <div className="teacher-shell teacher-content">{children}</div>
      </main>
    </>
  );
}

export function TeacherBanner({ title, description }) {
  return (
    <section className="teacher-banner">
      <h2>{title}</h2>
      <p>{description}</p>
    </section>
  );
}

export function TeacherCard({ title, description, actions, children, className = "" }) {
  return (
    <section className={`teacher-card ${className}`.trim()}>
      {title || description || actions ? (
        <div className="teacher-card-header">
          <div>
            {title ? <h3>{title}</h3> : null}
            {description ? <p>{description}</p> : null}
          </div>
          {actions ? <div className="teacher-section-actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function TeacherLoadingState({ label = "Loading..." }) {
  return (
    <div className="teacher-loading-state">
      <Loader2 size={28} />
      <div>{label}</div>
    </div>
  );
}

export function TeacherEmptyState({ title, description, action }) {
  return (
    <div className="teacher-empty-state">
      <AlertCircle size={28} />
      <div>
        <strong>{title}</strong>
      </div>
      <div>{description}</div>
      {action}
    </div>
  );
}

export function TeacherKeyValueGrid({ entries = [] }) {
  if (!entries.length) {
    return (
      <TeacherEmptyState
        title="No profile data"
        description="The backend did not return any displayable teacher fields."
      />
    );
  }

  return (
    <div className="teacher-key-value-grid">
      {entries.map((entry) => (
        <div key={entry.key} className="teacher-key-value-item">
          <span className="teacher-key-value-label">
            {entry.label || humanizeTeacherField(entry.key)}
          </span>
          <span className="teacher-key-value-value">
            {formatTeacherValue(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function TeacherSummaryCard({ title, value, helper }) {
  return (
    <TeacherCard className="teacher-stat-card">
      <div className="teacher-muted">{title}</div>
      <div className="teacher-stat-value">{value}</div>
      {helper ? <div className="teacher-muted">{helper}</div> : null}
    </TeacherCard>
  );
}

export function TeacherProfileHero({ teacher }) {
  const displayName = getTeacherDisplayName(teacher);
  const displayEmail = getTeacherEmail(teacher);
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <div className="teacher-list-card">
      <div className="teacher-list-card-main">
        <div className="teacher-list-card-heading">
          <div className="teacher-avatar">{initials || "T"}</div>
          <div>
            <h3>{displayName}</h3>
            <p>{displayEmail || "Email not provided"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function buildTeacherEntries(teacher = {}, prioritizedKeys = []) {
  const priorityEntries = prioritizedKeys
    .filter((key) => Object.prototype.hasOwnProperty.call(teacher, key))
    .map((key) => ({
      key,
      label: humanizeTeacherField(key),
      value: teacher[key],
    }));

  const seenKeys = new Set(priorityEntries.map((entry) => entry.key));

  const remainingEntries = Object.entries(teacher)
    .filter(([key, value]) => {
      if (seenKeys.has(key)) return false;
      if (typeof value === "function") return false;
      if (value === null || typeof value === "undefined" || value === "") return false;
      return !isLargeObject(value);
    })
    .map(([key, value]) => ({
      key,
      label: humanizeTeacherField(key),
      value,
    }));

  return [...priorityEntries, ...remainingEntries];
}

function isLargeObject(value) {
  if (!value || typeof value !== "object") return false;
  if (Array.isArray(value)) return false;
  return Object.keys(value).length > 8;
}
